import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ArrowLeft, Edit, Trash2, ArrowUp, ArrowDown, Clock, Sparkles } from 'lucide-react';
import { BottomNav } from '@/components/BottomNav';

const categoryColors = {
  outdoors: 'bg-categoryOutdoors-bg text-categoryOutdoors-text border-categoryOutdoors-text/20',
  crafts: 'bg-categoryCrafts-bg text-categoryCrafts-text border-categoryCrafts-text/20',
  cooking: 'bg-categorySocial-bg text-categorySocial-text border-categorySocial-text/20',
  painting: 'bg-categoryPainting-bg text-categoryPainting-text border-categoryPainting-text/20',
  social: 'bg-categorySocial-bg text-categorySocial-text border-categorySocial-text/20',
  exercise: 'bg-categoryOutdoors-bg text-categoryOutdoors-text border-categoryOutdoors-text/20',
  learning: 'bg-categoryCrafts-bg text-categoryCrafts-text border-categoryCrafts-text/20',
  entertainment: 'bg-categoryPainting-bg text-categoryPainting-text border-categoryPainting-text/20',
  relaxation: 'bg-categorySocial-bg text-categorySocial-text border-categorySocial-text/20'
};

const MyIdeasPage = () => {
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyIdeas();
  }, []);

  const fetchMyIdeas = async () => {
    try {
      const response = await axios.get('/ideas/my-ideas');
      setIdeas(response.data);
    } catch (error) {
      toast.error('Failed to fetch your ideas');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (ideaId) => {
    try {
      await axios.delete(`/ideas/${ideaId}`);
      setIdeas(ideas.filter(idea => idea.id !== ideaId));
      toast.success('Idea deleted');
    } catch (error) {
      toast.error('Failed to delete idea');
    }
  };

  return (
    <div className="min-h-screen bg-surface pb-24 md:pb-0">
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="sticky top-0 z-40 bg-white shadow-soft border-b-2 border-grayLight"
      >
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-4">
          <motion.button
            whileHover={{ scale: 1.1, x: -5 }}
            whileTap={{ scale: 0.9 }}
            data-testid="back-to-feed-btn"
            onClick={() => navigate('/')}
            className="p-2 rounded-md hover:bg-grayLight transition-colors focus-ring"
          >
            <ArrowLeft className="w-5 h-5" />
          </motion.button>
          <h1 className="text-2xl font-baloo font-bold text-grayDark">My Ideas</h1>
        </div>
      </motion.header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-12">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-12 h-12 border-4 border-primary border-t-transparent rounded-lg mx-auto"
            />
            <p className="text-gray mt-4 font-bold">Loading your ideas...</p>
          </div>
        ) : ideas.length === 0 ? (
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg shadow-soft p-12 text-center border border-grayLight"
          >
            <div className="w-20 h-20 bg-gradient-to-br from-primary to-accentCyan rounded-lg mx-auto mb-4 flex items-center justify-center shadow-soft">
              <Sparkles className="w-10 h-10 text-primaryDark" />
            </div>
            <h3 className="font-baloo font-bold text-xl text-grayDark mb-2">No ideas yet!</h3>
            <p className="text-gray mb-6">Start sharing your creativity</p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                data-testid="submit-first-idea-btn"
                onClick={() => navigate('/')}
                className="bg-primary hover:bg-primary/90 text-primaryDark border-2 border-primaryDark rounded-md px-6 shadow-soft font-bold"
              >
                Go to Feed
              </Button>
            </motion.div>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {ideas.map((idea, index) => (
              <motion.article
                key={idea.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                data-testid={`my-idea-card-${index}`}
                className="bg-white rounded-lg shadow-sm p-5 border border-cardBorder"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Badge className={`text-xs px-2 py-0.5 border font-semibold rounded-md ${categoryColors[idea.category]}`}>
                    {idea.category}
                  </Badge>
                  <span className="text-gray text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {idea.time_needed}
                  </span>
                </div>

                <div className="mb-4">
                  <h3 className="font-baloo font-bold text-primaryDark text-lg mb-1">{idea.title}</h3>
                  <p className="text-gray text-sm leading-relaxed">{idea.description}</p>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-sm text-gray">
                    <span className="flex items-center gap-1">
                      <ArrowUp className="w-4 h-4 text-voteUp-text" />
                      {idea.upvotes}
                    </span>
                    <span className="flex items-center gap-1">
                      <ArrowDown className="w-4 h-4 text-voteDown-text" />
                      {idea.downvotes}
                    </span>
                    <span className="font-semibold text-grayMeta">
                      {idea.upvotes - idea.downvotes} points
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      data-testid={`edit-btn-${index}`}
                      onClick={() => navigate(`/edit/${idea.id}`)}
                      size="sm"
                      className="h-9 bg-categoryPainting-bg hover:bg-categoryPainting-text/10 text-categoryPainting-text border border-categoryPainting-text/30 rounded-lg shadow-sm font-semibold transition-colors"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          data-testid={`delete-btn-${index}`}
                          size="sm"
                          variant="outline"
                          className="h-9 bg-voteDown-bg hover:bg-voteDown-text text-voteDown-text hover:text-white border border-voteDown-text/30 rounded-lg font-semibold transition-colors"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-lg border border-cardBorder">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-baloo font-bold text-xl text-primaryDark">Delete this idea?</AlertDialogTitle>
                          <AlertDialogDescription className="text-gray text-base">
                            This action cannot be undone. This will permanently delete your idea.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter className="gap-2">
                          <AlertDialogCancel 
                            data-testid={`delete-cancel-${index}`} 
                            className="rounded-lg border border-cardBorder hover:bg-surface font-semibold text-primaryDark"
                          >
                            Cancel
                          </AlertDialogCancel>
                          <AlertDialogAction
                            data-testid={`delete-confirm-${index}`}
                            onClick={() => handleDelete(idea.id)}
                            className="bg-voteDown-text hover:bg-voteDown-text/90 text-white rounded-lg font-semibold shadow-sm"
                          >
                            Delete Permanently
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </main>

      <BottomNav onCreateClick={() => navigate('/')} />
    </div>
  );
};

export default MyIdeasPage;
